import React from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { SxProps, Theme } from '@mui/material/styles';
import { useSynchronizedReveal } from 'hooks/useSynchronizedReveal';

const REVEAL_MS = 300;
const REVEAL_EASING = 'ease-out';

export type ViewRevealerMode = 'block' | 'overlay';

type ViewRevealerProps = {
  loading: boolean;
  /** block: show only spinner until ready (children not mounted). overlay: dim + spinner over children. */
  mode?: ViewRevealerMode;
  children: React.ReactNode;
  minHeight?: number | string;
  loader?: React.ReactNode;
  sx?: SxProps<Theme>;
  contentSx?: SxProps<Theme>;
};

/**
 * Unified “wait for data, then fade in” for Chat / Template (and similar) views.
 * On failure, set loading false anyway so the shell still reveals into your error UI.
 * Default spinner is scoped (not full-screen) so sidebar navigation stays clickable.
 */
export default function ViewRevealer({
  loading,
  mode = 'block',
  children,
  minHeight = 360,
  loader,
  sx,
  contentSx,
}: ViewRevealerProps) {
  const revealed = useSynchronizedReveal(loading);

  const spinner = loader ?? <CircularProgress color="primary" size={40} />;

  if (mode === 'block' && loading) {
    return (
      <Box
        sx={{
          width: '100%',
          minHeight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...sx,
        }}
      >
        {spinner}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: mode === 'overlay' ? '100%' : undefined,
        minHeight: mode === 'overlay' ? 0 : undefined,
        ...sx,
      }}
    >
      {mode === 'overlay' && loading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'action.hover',
            pointerEvents: 'none',
          }}
        >
          {spinner}
        </Box>
      )}
      <Box
        sx={{
          height: mode === 'overlay' ? '100%' : undefined,
          opacity: loading ? 0 : revealed ? 1 : 0,
          transition: `opacity ${REVEAL_MS}ms ${REVEAL_EASING}`,
          pointerEvents: loading ? 'none' : 'auto',
          ...contentSx,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
